import React, { useEffect, useRef, useState } from 'react';
import { Send, Sun, Moon, Bot, Sparkles, MessageSquare, Calendar, Mail, CheckCircle, Github, Linkedin, Globe } from 'lucide-react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { useTheme } from './src/ThemeContext.jsx';

/**
 * Agentic AI Assistant Chat Page — Advanced UI/UX
 * - Incorporates Contact page's sophisticated design elements
 * - TiltCard effects, Magnetic buttons, ParallaxField
 * - Enhanced chat interface with Contact page styling
 * - Responsive design with dark/light mode support
 */

export default function AgenticChatPage() {
  const { darkMode, toggleTheme } = useTheme();
  
  // Chat state
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Ask any question.",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      text: newMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsTyping(true);

    // Simulate bot response
    setTimeout(() => {
      const botResponses = [
        "That's a great question! AI chatbots can be customized for various industries and use cases.",
        "I can help with customer support, lead generation, appointment booking, and much more!",
        "The development timeline typically ranges from 2-8 weeks depending on complexity.",
        "Integration with existing systems like CRM, databases, and APIs is definitely possible.",
        "Would you like to know more about pricing or specific features?",
        "I use natural language processing to understand and respond to user queries effectively.",
        "I can assist with building autonomous AI agents that handle complex workflows.",
        "My expertise includes RAG systems, multi-agent architectures, and LLM integration."
      ];
      
      const randomResponse = botResponses[Math.floor(Math.random() * botResponses.length)];
      
      const botMessage = {
        id: messages.length + 2,
        text: randomResponse,
        sender: 'bot',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    }, 1000 + Math.random() * 2000);
  };

  return (
    <div className={darkMode ? 'min-h-screen bg-[#0a0c10] text-white' : 'min-h-screen bg-white text-gray-900'}>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] px-3 py-2 rounded-md bg-indigo-600 text-white">Skip to content</a>

      {/* NAV — same as Contact page */}
      <header className="sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto mt-4 px-4 sm:px-6">
          <div className={`backdrop-blur rounded-2xl shadow-sm flex items-center justify-between px-4 sm:px-6 py-3 ${darkMode ? 'bg-white/5 border border-white/10' : 'bg-white/80 border border-white/90'}`}>
            <a href="/" className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-semibold text-white ${darkMode ? 'bg-gradient-to-br from-indigo-500 to-cyan-400' : 'bg-gradient-to-br from-indigo-600 to-cyan-500 drop-shadow'}`}>ST</div>
              <div className="leading-tight">
                <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Sikandar Tareen</div>
                <div className={`text-[11px] ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>Agentic AI Engineer</div>
              </div>
            </a>
            <div className={`hidden md:flex items-center gap-6 text-sm ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>
              <a href="/" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>Home</a>
              <a href="/#projects" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>Projects</a>
              <a href="/blog" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>Blog</a>
              <a href="/about-me" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>About</a>
              <a href="/contact" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-blue-600 text-white">
                <Sparkles size={14} /> Contact Me
              </a>
            </div>
          </div>
        </nav>
      </header>

      {/* PARALLAX SPARKLES */}
      <ParallaxField darkMode={darkMode} />

      <main id="main" className="max-w-7xl mx-auto px-4 sm:px-6 pb-20 pt-10 [perspective:1200px]">
        {/* Hero Section — Contact page style */}
        <section className="relative overflow-visible">
          <div className={`absolute -top-24 -left-24 w-[42rem] h-[42rem] rounded-full blur-3xl ${darkMode ? 'bg-indigo-600/20' : 'bg-indigo-200/60'}`}></div>
          <div className={`absolute -bottom-32 -right-16 w-[36rem] h-[36rem] rounded-full blur-3xl ${darkMode ? 'bg-cyan-500/20' : 'bg-cyan-200/60'}`}></div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative z-10">
            <h1 className="text-4xl sm:text-5xl font-extrabold">Agentic AI Assistant</h1>
            <p className={`mt-3 text-lg max-w-2xl ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>Ask any question to my Agentic AI Assistant. It's trained on my portfolio—ask about projects, skills, or hobbies.</p>
          </motion.div>
        </section>



        {/* Chat Interface — Enhanced with Contact page styling */}
        <section id="chat-interface" className="mt-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Chat */}
            <div className="lg:col-span-2">
              <TiltCard darkMode={darkMode} glow className={`${darkMode ? 'bg-white/5 border-white/10 text-white hover:shadow-cyan-900/30' : 'bg-white border-gray-200 text-gray-900 hover:shadow-indigo-200'} rounded-2xl p-6 border h-[600px] flex flex-col`}>
                <div className={`mb-4 pb-4 border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                  <div className={`${darkMode ? 'from-indigo-600/30 to-cyan-500/30 border-white/10 text-white' : 'from-indigo-100 to-cyan-100 border-gray-200 text-gray-900'} rounded-lg p-4 bg-gradient-to-r border`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-semibold text-white text-sm ${darkMode ? 'bg-gradient-to-br from-indigo-500 to-cyan-400' : 'bg-gradient-to-br from-indigo-600 to-cyan-500 drop-shadow'}`}>
                        ST
                      </div>
                      <div>
                        <h4 className="font-semibold">AI Assistant</h4>
                        <p className={`${darkMode ? 'text-white/80' : 'text-gray-700'} text-sm mt-1`}>Powered by OpenAI</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto mb-4 space-y-4">
                  {messages.map((message) => (
                    <div key={message.id} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-lg ${message.sender === 'user' ? 'bg-blue-600 text-white' : darkMode ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'}`}>
                        <p className="text-sm">{message.text}</p>
                        <p className={`text-xs mt-1 ${message.sender === 'user' ? 'text-blue-100' : darkMode ? 'text-white/50' : 'text-gray-500'}`}>
                          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                  
                  {isTyping && (
                    <div className="flex justify-start">
                      <div className={`p-3 rounded-lg ${darkMode ? 'bg-white/10' : 'bg-gray-100'}`}>
                        <div className="flex space-x-1">
                          <div className={`w-2 h-2 rounded-full animate-bounce ${darkMode ? 'bg-white/60' : 'bg-gray-600'}`} style={{ animationDelay: '0ms' }}></div>
                          <div className={`w-2 h-2 rounded-full animate-bounce ${darkMode ? 'bg-white/60' : 'bg-gray-600'}`} style={{ animationDelay: '150ms' }}></div>
                          <div className={`w-2 h-2 rounded-full animate-bounce ${darkMode ? 'bg-white/60' : 'bg-gray-600'}`} style={{ animationDelay: '300ms' }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={chatEndRef} />
                </div>

                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    className={`flex-1 px-4 py-3 rounded-lg border ${darkMode ? 'bg-white/5 border-white/10 text-white placeholder-white/50' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                  />
                  <Magnetic>
                    <button
                      type="submit"
                      disabled={!newMessage.trim()}
                      className={`px-4 py-3 rounded-lg bg-blue-600 text-white hover:opacity-95 transition focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <Send size={16} />
                    </button>
                  </Magnetic>
                </form>
              </TiltCard>
            </div>

            {/* Sidebar Info — Contact page style */}
            <div className="space-y-6">
              <TiltCard darkMode={darkMode} className={`${darkMode ? 'from-indigo-600/30 to-cyan-500/30 border-white/10 text-white' : 'from-indigo-100 to-cyan-100 border-gray-200 text-gray-900'} rounded-2xl p-6 bg-gradient-to-r border`}>
                <h4 className="font-semibold mb-3">Resume</h4>
                <p className={`${darkMode ? 'text-white/80' : 'text-gray-700'} text-sm mb-4`}>Get my latest Resume (PDF).</p>
                <Magnetic>
                  <a href="/contact" className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white shadow hover:opacity-95 transition focus:outline-none focus-visible:ring-2 ${darkMode ? 'focus-visible:ring-cyan-300/60' : 'focus-visible:ring-indigo-500'}`}>
                    <Mail size={14}/> Download
                  </a>
                </Magnetic>
              </TiltCard>

              <TiltCard darkMode={darkMode} className={`${darkMode ? 'from-indigo-600/30 to-cyan-500/30 border-white/10 text-white' : 'from-indigo-100 to-cyan-100 border-gray-200 text-gray-900'} rounded-2xl p-6 bg-gradient-to-r border`}>
                <h4 className="font-semibold mb-3">Connect With Me</h4>
                <p className={`${darkMode ? 'text-white/80' : 'text-gray-700'} text-sm mb-4`}>Follow my work and connect with me on various platforms.</p>
                <div className="flex gap-3 justify-start">
                  {[
                    { icon: <Github size={20} />, href: 'https://github.com/sikandarktareen', label: 'GitHub' },
                    { icon: <Linkedin size={20} />, href: 'https://www.linkedin.com/in/sikandar-tareen-54b519134/', label: 'LinkedIn' },
                    { icon: <Globe size={20} />, href: '#', label: 'Portfolio' },
                  ].map((social) => (
                    <Magnetic key={social.label}>
                      <a
                        href={social.href}
                        className={`p-2 rounded-lg ${darkMode ? 'text-white hover:bg-white/10 hover:scale-110' : 'text-gray-700 hover:bg-gray-100 hover:scale-110'} transition-all duration-300 hover:shadow-md`}
                      >
                        {social.icon}
                      </a>
                    </Magnetic>
                  ))}
                </div>
              </TiltCard>

              <TiltCard darkMode={darkMode} className={`${darkMode ? 'from-indigo-600/30 to-cyan-500/30 border-white/10 text-white' : 'from-indigo-100 to-cyan-100 border-gray-200 text-gray-900'} rounded-2xl p-6 bg-gradient-to-r border`}>
                <h4 className="font-semibold mb-3">Let's Connect</h4>
                <p className={`${darkMode ? 'text-white/80' : 'text-gray-700'} text-sm mb-4`}>Book a free 30-minute video or phone call</p>
                <Magnetic>
                  <a href="/schedule-call" className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white shadow hover:opacity-95 transition focus:outline-none focus-visible:ring-2 ${darkMode ? 'focus-visible:ring-cyan-300/60' : 'focus-visible:ring-indigo-500'}`}>
                    <Calendar size={14}/> Book a Call
                  </a>
                </Magnetic>
              </TiltCard>
            </div>
          </div>
        </section>
      </main>

      <footer className={`py-10 text-sm ${darkMode ? 'text-white/60' : 'text-gray-600'} border-t ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">© {new Date().getFullYear()} Sikandar Tareen</div>
      </footer>

      {/* Theme toggle */}
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={toggleTheme}
          className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg border transition focus:outline-none focus:ring-2 ${darkMode ? 'border-white/20 bg-[#1a1c20] text-white hover:opacity-90 focus:ring-cyan-300/60' : 'border-gray-300 bg-white/95 backdrop-blur text-gray-800 hover:opacity-90 focus:ring-indigo-500'}`}
          aria-pressed={darkMode}
          aria-label="Toggle theme"
        >
          {darkMode ? <Sun size={16} /> : <Moon size={16} />}
          {darkMode ? 'Light Mode' : 'Dark Mode'}
        </button>
      </div>

      {/* Reduce motion support */}
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          * { animation: none !important; transition: none !important; }
        }
      `}</style>
    </div>
  );
}

function TiltCard({ children, className = '', glow = false, darkMode = false }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useMotionValue(0), { stiffness: 300, damping: 30 });
  const rotateY = useSpring(useMotionValue(0), { stiffness: 300, damping: 30 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleMouseMove = (event) => {
      const rect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const mouseX = event.clientX - centerX;
      const mouseY = event.clientY - centerY;

      x.set(mouseX * 0.1);
      y.set(mouseY * 0.1);
      rotateX.set(mouseY * -0.05);
      rotateY.set(mouseX * 0.05);
    };

    const handleMouseLeave = () => {
      x.set(0);
      y.set(0);
      rotateX.set(0);
      rotateY.set(0);
    };

    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [x, y, rotateX, rotateY]);

  return (
    <motion.div
      ref={ref}
      style={{ rotateX, rotateY }}
      className={`transform-gpu transition-shadow duration-300 ${glow ? 'hover:shadow-2xl' : ''} ${className}`}
    >
      {children}
    </motion.div>
  );
}

function Magnetic({ children }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 300, damping: 30 });
  const springY = useSpring(y, { stiffness: 300, damping: 30 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleMouseMove = (event) => {
      const rect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const distance = Math.sqrt(
        Math.pow(event.clientX - centerX, 2) + Math.pow(event.clientY - centerY, 2)
      );

      if (distance < 100) {
        x.set((event.clientX - centerX) * 0.3);
        y.set((event.clientY - centerY) * 0.3);
      }
    };

    const handleMouseLeave = () => {
      x.set(0);
      y.set(0);
    };

    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [x, y]);

  return (
    <motion.div ref={ref} style={{ x: springX, y: springY }}>
      {children}
    </motion.div>
  );
}

function ParallaxField({ darkMode }) {
  const sparkles = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 3
  }));

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {sparkles.map((sparkle) => (
        <motion.div
          key={sparkle.id}
          className={`absolute w-1 h-1 rounded-full ${darkMode ? 'bg-cyan-400/30' : 'bg-indigo-400/20'}`}
          style={{
            left: `${sparkle.x}%`,
            top: `${sparkle.y}%`
          }}
          animate={{
            opacity: [0, 1, 0],
            scale: [0, 1, 0]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            delay: sparkle.delay
          }}
        />
      ))}
    </div>
  );
}